import {
  Drawer,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from "@mantine/core"
import { Dropzone } from "@mantine/dropzone"
import { useForm, zodResolver } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { IconFileCertificate, IconUpload, IconX } from "@tabler/icons-react"
import type { CountryIso3166 } from "@tfg/db"
import { Button, CountrySelector } from "@tfg/ui"
import { AxiosError } from "axios"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { CertificateCategory } from "@tfg/types/schemas/certificates"
import {
  prodSiteSchema,
  prodSiteValidationType,
} from "@tfg/types/schemas/productionSites"

import { useCreateProductionSiteMutation } from "~/pages/SupplierPortal/ProductionSites/hooks/useCreateProductionSiteMutation.ts"
import { useListCertificateTypesQuery } from "~/pages/SupplierPortal/ProductionSites/hooks/useListCertificteTypesQuery.ts"
import { COUNTRIES } from "~/utils/countries.ts"

interface Props {
  defaultName: string
  opened: boolean
  onClose: () => void
  onProductionSiteCreated: (productionSiteId: string) => void
}

export const ProductionSiteDrawer = ({
  defaultName,
  opened,
  onClose,
  onProductionSiteCreated,
}: Props) => {
  const { colors } = useMantineTheme()
  const { t } = useTranslation(["traceability"])
  const { data: certificateTypes } = useListCertificateTypesQuery({
    category: CertificateCategory.SCOPE,
  })
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const { mutate, isLoading } = useCreateProductionSiteMutation({
    onSuccess: (data) => {
      onProductionSiteCreated(data.id)
      handleClose()
    },
    onError: (error: AxiosError) => {
      notifications.show({
        message: error.response?.data?.toString() ?? error.message,
        color: "red",
      })
    },
  })
  const form = useForm<prodSiteValidationType>({
    validate: zodResolver(prodSiteSchema),
    initialValues: {
      name: defaultName,
      address: "",
      countryIso3166: "" as CountryIso3166,
      certificates: [],
    },
  })
  const onSubmit = (values: prodSiteValidationType) => {
    const certificates = selectedFiles
      .map((file, index) => ({
        file,
        typeId: values.certificates[index].type,
      }))
      .filter(({ typeId }) => typeId)
    mutate({
      request: values,
      certificates,
    })
  }
  const handleClose = () => {
    form.reset()
    onClose()
  }

  const isSubmittable =
    form.values.name && form.values.address && form.values.countryIso3166

  return (
    <Drawer
      position="right"
      opened={opened}
      onClose={handleClose}
      title={<Title order={3}>{t("productionSiteModal.title")}</Title>}
      size="xl"
    >
      <form onSubmit={form.onSubmit((values) => onSubmit(values))}>
        <Stack gap="xl">
          <Stack gap="sm">
            <Title order={4}>
              {t("productionSiteModal.titles.information")}
            </Title>
            <TextInput
              {...form.getInputProps("name")}
              label={t("productionSiteModal.labels.name")}
            />
            <TextInput
              {...form.getInputProps("address")}
              label={t("productionSiteModal.labels.address")}
            />
            <CountrySelector
              label={t("productionSiteModal.labels.country")}
              searchable
              countriesMapper={COUNTRIES}
              onChange={form.getInputProps("countryIso3166").onChange}
              value={form.getInputProps("countryIso3166").value}
            />
          </Stack>
          <Stack gap="sm">
            <Title order={4}>
              {t("productionSiteModal.titles.certificate")}
            </Title>
            <Dropzone
              onDrop={(newFiles) => {
                newFiles.forEach(() =>
                  form.insertListItem("certificates", { type: null })
                )
                setSelectedFiles((files) => {
                  return [...files, ...newFiles]
                })
              }}
              w="100%"
            >
              <Group justify="center">
                <Dropzone.Accept>
                  <IconUpload color={colors.green[6]} />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX color={colors.red[6]} />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconFileCertificate />
                </Dropzone.Idle>
                <Text>{t("productionSiteModal.actions.addCertificate")}</Text>
              </Group>
            </Dropzone>
            <Group justify="space-between" w="100%">
              <Stack gap="xs" justify="center" w="100%">
                <Stack gap="xs" w="100%">
                  {selectedFiles.map((file, index) => (
                    <Group
                      key={index}
                      justify="space-between"
                      w="100%"
                      wrap="nowrap"
                    >
                      <Text mt={24} size="sm">
                        {file.name}
                      </Text>
                      <Group align="center">
                        <Select
                          w={300}
                          searchable
                          label="Type"
                          data={
                            certificateTypes?.map((type) => ({
                              label: type.name,
                              value: type.id.toString(),
                            })) ?? []
                          }
                          {...form.getInputProps(`certificates.${index}.type`)}
                        />
                        <Button
                          size="xs"
                          variant="tertiary-critical"
                          mt={24}
                          onClick={() => {
                            form.removeListItem("certificates", index)
                            setSelectedFiles((files) => {
                              return files.filter((_file, i) => index !== i)
                            })
                          }}
                        >
                          {t("productionSiteModal.actions.remove")}
                        </Button>
                      </Group>
                    </Group>
                  ))}
                </Stack>
              </Stack>
            </Group>
          </Stack>
          <Group justify="right">
            <Button variant="tertiary" onClick={handleClose}>
              {t("productionSiteModal.actions.cancel")}
            </Button>
            <Button type="submit" loading={isLoading} disabled={!isSubmittable}>
              {t("productionSiteModal.actions.create")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  )
}
